import dynamic from 'next/dynamic'
import { DeepPartial } from 'utility-types'
import classNames from 'classnames'
import Text from '@/stories/components/Common/NewText'

const ChartLoader = () => (
  <div className="mb-4 h-96 animate-pulse rounded bg-gray-100" />
)

const DynamicChartWithNoSSR = dynamic(() => import('./TargetChart'), {
  ssr: false,
  loading: ChartLoader,
})

const TextLables = ({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color: string
}) => (
  <div className="flex flex-col py-2">
    <Text appearance="body1" addClassName="text-ind-black">
      {label}
    </Text>
    <Text appearance="subtitle1" style={{ color }}>
      {value}
    </Text>
  </div>
)

const ListItem = ({
  label,
  value,
  subValue,
}: {
  label?: string
  value?: string
  subValue?: string
}) => (
  <div className="flex flex-col gap-y-1">
    {label && (
      <Text appearance="subtitle1" addClassName="text-brand-grey-dark">
        {label}
      </Text>
    )}
    {value && (
      <Text appearance="heading2">
        {value}
        {subValue && (
          <Text
            appearance="caption"
            tag="span"
            addClassName="pl-1 text-brand-grey-base"
          >
            {subValue}
          </Text>
        )}
      </Text>
    )}
  </div>
)
// eslint-disable-next-line max-lines-per-function
export default function TargetPrice({
  data = [],
  list,
  high,
  low,
  mean,
  title,
  sub_title,
  growth_per,
  current,
  live_price,
  name,
  num_analysts,
  currencySymbol = '₹',
}: DeepPartial<{
  title: string
  sub_title: string
  high: number
  low: number
  mean: number
  current: number
  growth_per: number
  data: DeepPartial<
    {
      price: number
      label1: string
      label2: string
    }[]
  >
  list?: {
    label?: string
    value?: string
    subValue?: string
  }[]
  live_price?: number | string
  name?: string
  num_analysts?: number
  currencySymbol?: '₹' | '$'
}>) {
  if (!data.length) {
    return null
  }
  const modifiedData = [data[data.length - 1]]

  return (
    <div>
      {title && (
        <Text appearance="subtitle2" addClassName="text-ind-black">
          {title}
        </Text>
      )}
      {sub_title && (
        <Text appearance="subtitle1" addClassName="text-grey py-1">
          {sub_title}
          {typeof growth_per === 'number' && !Number.isNaN(growth_per) && (
            <Text
              tag="span"
              addClassName={classNames('pl-1', {
                'text-green-dark': growth_per >= 0,
                'text-red': growth_per < 0,
              })}
              appearance="subtitle1"
            >
              {`${growth_per.toFixed(2)}%`}
            </Text>
          )}
        </Text>
      )}
      {!!list?.length && (
        <div className="mb-8 grid grid-cols-2 gap-4 lg:mb-10 lg:grid-cols-4">
          {list.map(item => (
            <ListItem key={item.label} {...item} />
          ))}
        </div>
      )}
      <div className="flex flex-row ">
        <div className="max-w-5xl flex-1">
          <DynamicChartWithNoSSR
            modifiedData={modifiedData as any}
            data={data}
            low={low}
            high={high}
            mean={mean}
            currencySymbol={currencySymbol}
          />
        </div>
        <div className="flex flex-col pl-1">
          {high && (
            <TextLables
              label="High"
              value={`${currencySymbol}${high}`}
              color="#33A34D"
            />
          )}
          {mean && (
            <TextLables
              label="Target"
              value={`${currencySymbol}${mean.toFixed(2)}`}
              color="#017AFF"
            />
          )}
          {low && (
            <TextLables
              label="Low"
              value={`${currencySymbol}${low}`}
              color="#DF3C27"
            />
          )}
        </div>
      </div>
      <div className="flex flex-row pt-2">
        <Text appearance="body2" addClassName="text-ind-black">
          {name} target price {currencySymbol}
          {mean}, a slight {(growth_per ?? 0) >= 0 ? 'upside' : 'downside'} of{' '}
          {growth_per}% compared to current price of{' '}
          {live_price ? live_price : current}. According to {num_analysts}{' '}
          analysts rating.
        </Text>
      </div>
    </div>
  )
}
